'use client';

import { useState } from 'react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CharacterPricingPanel } from "@/components/translate/character-pricing-panel";
import { Zap, FileText, Bolt, SearchCheck } from "lucide-react";
import { useTranslations } from "next-intl";

interface RegisteredLimitDialogProps {
  isOpen: boolean;
  onClose: () => void;
  availableCharacters: number;
  attemptedCharacters?: number;
  insufficientBy?: number;
}

export function RegisteredLimitDialog({
  isOpen,
  onClose,
  availableCharacters,
  attemptedCharacters = 0,
  insufficientBy = 0
}: RegisteredLimitDialogProps) {
  const t = useTranslations('limitDialogs.registered');
  const [isPricingOpen, setIsPricingOpen] = useState(false);

  const handleDialogChange = (open: boolean) => {
    if (!open) {
      setIsPricingOpen(false);
      onClose();
    }
  };

  const handleClose = () => {
    setIsPricingOpen(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleDialogChange}>
      <DialogContent className="sm:max-w-[450px] max-h-[90vh] flex flex-col overflow-hidden transition-all duration-300 ease-in-out">
        <DialogHeader className="flex-shrink-0">
          <div className="flex items-center gap-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-500/10">
              <Zap className="h-5 w-5 text-blue-500" />
            </div>
            <div>
              <DialogTitle className="text-start">{t('title')}</DialogTitle>
              <DialogDescription className="text-start">
                {isPricingOpen ? t('pricingDescription') : t('description')}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div
          key={isPricingOpen ? 'pricing' : 'default'}
          className="space-y-6 overflow-y-auto flex-1 min-h-0 -mx-6 px-6 animate-in fade-in-0 slide-in-from-bottom-1 duration-200"
        >
          {isPricingOpen ? (
            <CharacterPricingPanel onTermsClick={handleClose} />
          ) : (
            <>
              {/* Current Usage */}
              <div className="rounded-lg border bg-muted/50 p-4">
                <div className="text-sm font-medium mb-2">{t('creditSnapshot')}</div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">{t('availableCredits')}</span>
                    <Badge variant="secondary">{availableCharacters.toLocaleString()}</Badge>
                  </div>
                  {attemptedCharacters > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">{t('attemptedTranslation')}</span>
                      <Badge variant="destructive" className="bg-red-500/10 text-red-600 hover:bg-red-500/20">
                        {attemptedCharacters.toLocaleString()}
                      </Badge>
                    </div>
                  )}
                  {insufficientBy > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">{t('insufficientBy')}</span>
                      <Badge variant="destructive" className="bg-red-500/10 text-red-600 hover:bg-red-500/20">
                        {insufficientBy.toLocaleString()}
                      </Badge>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-2 flex-shrink-0">
          {!isPricingOpen && (
            <>
              <Button onClick={() => setIsPricingOpen(true)} className="w-full">
                {t('getMoreCharacters')}
              </Button>
              <Button onClick={handleClose} variant="outline" className="w-full">
                {t('gotIt')}
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
